import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './';

/**
 * MIME Request Card - Displays a pending MIME request with countdown timer
 * Used on the Guest Dashboard to show urgent re-commitment requests
 */
export default function MimeRequestCard({
  request,
  onAccept,
  onDecline,
  isResponding = false,
  error = null,
  declineLabel = 'DECLINE',
}) {
  const navigate = useNavigate();
  const [secondsRemaining, setSecondsRemaining] = useState(request?.secondsRemaining || 0);
  const [isExpired, setIsExpired] = useState(request?.isExpired || false);

  // Countdown timer
  useEffect(() => {
    if (isExpired || secondsRemaining <= 0) {
      setIsExpired(true);
      return;
    }

    const timer = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          setIsExpired(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isExpired, secondsRemaining]);

  // Format time as MM:SS or HH:MM:SS
  const formatTime = (seconds) => {
    if (seconds <= 0) return '00:00';

    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Format $ amount
  const formatAmount = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Get urgency color based on time remaining
  const getUrgencyColor = () => {
    if (isExpired) return 'border-gray-400 bg-gray-100';
    if (secondsRemaining < 300) return 'border-red-500 bg-red-50'; // < 5 min
    if (secondsRemaining < 900) return 'border-yellow-500 bg-yellow-50'; // < 15 min
    return 'border-black bg-white';
  };

  const getTimerColor = () => {
    if (isExpired) return 'text-gray-500';
    if (secondsRemaining < 300) return 'text-red-600';
    if (secondsRemaining < 900) return 'text-yellow-600';
    return 'text-black';
  };

  const isInsufficientBalance = error && /insufficient|balance/i.test(error);

  if (!request) return null;

  return (
    <div className={`border-2 ${getUrgencyColor()} transition-colors`}>
      {/* Header with event info and timer */}
      <div className="px-4 py-3 border-b-2 border-current flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="font-mono text-sm font-bold uppercase tracking-wider text-black truncate">
            {request.eventTitle || 'Event'}
          </h3>
          {request.eventDateTime && (
            <p className="font-mono text-xs text-gray-600 uppercase tracking-wider">
              {formatDate(request.eventDateTime)}
            </p>
          )}
        </div>

        {/* Shot Clock */}
        <div className={`text-right ${getTimerColor()}`}>
          <div className="font-mono text-2xl font-bold tabular-nums">
            {isExpired ? 'EXPIRED' : formatTime(secondsRemaining)}
          </div>
          <p className="font-mono text-[10px] uppercase tracking-wider opacity-75">
            TIME LEFT
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {/* MIME label and amount */}
        <div className="flex items-center gap-3 mb-3">
          <span className="px-2 py-1 bg-black text-white font-mono text-xs font-bold uppercase tracking-wider">
            MIME
          </span>
          <span className="font-mono text-lg font-bold text-black">
            {formatAmount(request.additionalCommitmentCents)}
          </span>
        </div>

        {/* Message from host */}
        {request.message && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200">
            <p className="font-mono text-xs text-gray-600 uppercase tracking-wider mb-1">
              HOST MESSAGE
            </p>
            <p className="font-mono text-sm text-gray-800">
              {request.message}
            </p>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 border-2 border-red-600 bg-red-50">
            <p className="font-mono text-xs font-semibold text-red-600 mb-1">
              {error}
            </p>
            {isInsufficientBalance && (
              <button
                type="button"
                onClick={() => navigate('/wallet')}
                className="mt-2 px-4 py-2 bg-black text-white font-mono text-xs font-bold uppercase tracking-wider hover:bg-gray-800 transition-colors"
              >
                TOP UP $
              </button>
            )}
          </div>
        )}

        {/* Action buttons */}
        {!isExpired && request.status === 'Pending' && (
          <div className="flex gap-3">
            <Button
              variant="primary"
              size="md"
              onClick={() => onAccept?.(request.id)}
              disabled={isResponding}
              className="flex-1"
            >
              {isResponding ? 'PROCESSING...' : 'COMMIT'}
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={() => onDecline?.(request.id)}
              disabled={isResponding}
              className="flex-1"
            >
              {declineLabel}
            </Button>
          </div>
        )}

        {/* Status display for non-pending */}
        {request.status !== 'Pending' && (
          <div className={`
            px-4 py-3 text-center font-mono text-sm font-bold uppercase tracking-wider
            ${request.status === 'Accepted' ? 'bg-green-100 text-green-800' : ''}
            ${request.status === 'Declined' ? 'bg-gray-100 text-gray-600' : ''}
            ${request.status === 'Expired' ? 'bg-gray-100 text-gray-500' : ''}
          `}>
            {request.status === 'Accepted' && `COMMITTED ${formatAmount(request.committedAmountCents || request.additionalCommitmentCents)}`}
            {request.status === 'Declined' && 'DECLINED'}
            {request.status === 'Expired' && 'EXPIRED'}
          </div>
        )}

        {/* Expired state */}
        {isExpired && request.status === 'Pending' && (
          <div className="px-4 py-3 text-center font-mono text-sm font-bold uppercase tracking-wider bg-gray-100 text-gray-500">
            EXPIRED
          </div>
        )}
      </div>
    </div>
  );
}
